import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  Download,
  ChevronRight,
  Sparkles,
  Phone,
  Mail,
  Building,
} from 'lucide-react';
import RefreshButton from '../../components/layout/RefreshButton';
import Select from '../../components/ui/Select';
import { adminApi } from '../../services/adminApi';
import { exportToCSV, exportToJSON, prepareRequestsForExport } from '../../utils/export';

const STATUS_OPTIONS = [
  { value: '', label: 'All Status' },
  { value: 'new', label: 'New' },
  { value: 'contacted', label: 'Contacted' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'completed', label: 'Completed' },
  { value: 'cancelled', label: 'Cancelled' },
];

export default function RequestList() {
  const [requests, setRequests] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);

  const fetchRequests = useCallback(async () => {
    try {
      const response = await adminApi.getCustomRequests({
        page,
        limit: 20,
        status: statusFilter || undefined,
      });
      setRequests(response.requests || []);
      setTotalPages(response.totalPages || 1);
      setTotal(response.total || 0);
      setLastUpdated(new Date());
    } catch (err) {
      console.error('Failed to fetch requests:', err);
    }
  }, [page, statusFilter]);

  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true);
      await fetchRequests();
      setIsLoading(false);
    };
    loadData();
  }, [fetchRequests]);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await fetchRequests();
    setIsRefreshing(false);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'new':
        return 'badge-warning';
      case 'contacted':
        return 'badge-primary';
      case 'in_progress':
        return 'badge-primary';
      case 'completed':
        return 'badge-success';
      case 'cancelled':
        return 'badge-danger';
      default:
        return 'badge-gray';
    }
  };

  const handleExport = async (format) => {
    try {
      const response = await adminApi.getCustomRequests({ limit: 10000 });
      const exportData = prepareRequestsForExport(response.requests || []);

      if (format === 'csv') {
        exportToCSV(exportData, 'requests-export', [
          { key: 'name', label: 'Name' },
          { key: 'email', label: 'Email' },
          { key: 'phone', label: 'Phone' },
          { key: 'businessName', label: 'Business' },
          { key: 'agentType', label: 'Agent Type' },
          { key: 'status', label: 'Status' },
          { key: 'submittedDate', label: 'Submitted Date' },
        ]);
      } else {
        exportToJSON(exportData, 'requests-export');
      }
    } catch (err) {
      console.error('Export failed:', err);
    }
  };

  return (
    <div className="p-6 lg:p-8 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-text-primary">Custom Agent Requests</h1>
          <p className="text-text-secondary mt-1">
            Manage incoming custom agent requests ({total} total)
          </p>
        </div>
        <div className="flex items-center gap-3">
          <RefreshButton
            onClick={handleRefresh}
            isLoading={isRefreshing}
            lastUpdated={lastUpdated}
          />
          <div className="relative group">
            <button className="btn-secondary">
              <Download className="w-4 h-4" />
              Export
            </button>
            <div className="absolute right-0 mt-2 w-32 bg-white rounded-lg shadow-lg border border-border py-1 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-10">
              <button
                onClick={() => handleExport('csv')}
                className="w-full px-4 py-2 text-left text-sm hover:bg-bg-secondary"
              >
                Export CSV
              </button>
              <button
                onClick={() => handleExport('json')}
                className="w-full px-4 py-2 text-left text-sm hover:bg-bg-secondary"
              >
                Export JSON
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="card p-4 mb-6">
        <div className="flex gap-4">
          <div className="min-w-[180px]">
            <Select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setPage(1);
              }}
              options={STATUS_OPTIONS}
              placeholder="All Status"
            />
          </div>
        </div>
      </div>

      {/* Requests List */}
      <div className="card">
        {isLoading ? (
          <div className="p-4">
            <div className="animate-pulse space-y-4">
              {[1, 2, 3, 4, 5].map((i) => (
                <div key={i} className="h-20 bg-bg-secondary rounded"></div>
              ))}
            </div>
          </div>
        ) : requests.length === 0 ? (
          <div className="p-12 text-center text-text-muted">
            No custom requests found
          </div>
        ) : (
          <div className="divide-y divide-border">
            {requests.map((request) => (
              <Link
                key={request.id}
                to={`/requests/${request.id}`}
                className="p-4 flex items-start gap-4 hover:bg-bg-secondary/50 transition-colors"
              >
                <div className="w-12 h-12 rounded-xl bg-amber-100 flex items-center justify-center flex-shrink-0">
                  <Sparkles className="w-6 h-6 text-amber-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between mb-1">
                    <h3 className="font-semibold text-text-primary">
                      {request.name}
                    </h3>
                    <span className={`badge ${getStatusColor(request.status)}`}>
                      {request.status?.replace('_', ' ') || 'new'}
                    </span>
                  </div>
                  <div className="flex items-center gap-4 text-sm text-text-secondary mb-2">
                    {request.businessName && (
                      <span className="flex items-center gap-1">
                        <Building className="w-4 h-4" />
                        {request.businessName}
                      </span>
                    )}
                    <span className="flex items-center gap-1">
                      <Mail className="w-4 h-4" />
                      {request.email}
                    </span>
                    {request.phone && (
                      <span className="flex items-center gap-1">
                        <Phone className="w-4 h-4" />
                        {request.phone}
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-text-muted line-clamp-2">
                    {request.description}
                  </p>
                  <p className="text-xs text-text-muted mt-2">
                    Submitted {formatDate(request.createdAt)}
                  </p>
                </div>
                <ChevronRight className="w-5 h-5 text-text-muted flex-shrink-0" />
              </Link>
            ))}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="p-4 border-t border-border flex items-center justify-between">
            <p className="text-sm text-text-muted">
              Page {page} of {totalPages}
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="btn-ghost"
              >
                Previous
              </button>
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="btn-ghost"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
